<script setup lang="ts">
import PlusMenuLink from '@app/layout/nav/mobile/components/PlusMenuLink.vue';
import {
  URL_ADAF,
  URL_ADDRESS_PAGE,
  URL_INVOICES,
  URL_MANAGEMENT_INVOICES,
  URL_PAYOUTS,
  URL_SEARCH,
  URL_SELLER_ORGANIZATION_MANAGE,
  URL_SUBSCRIPTION,
  URL_USER_NOTIFICATIONS,
  URL_USER_OFFERS,
  URL_USER_ORGANIZATION_PAYMENT_ACCOUNT,
  URL_USER_ORGANIZATION_PAYMENT_METHOD_SELLER,
  URL_USER_ORGANIZATION_TEAM,
  URL_USER_PROFILE_INVOICE,
  URL_USER_PROFILE_MANAGE,
  URL_USER_LEADS,
} from '@tools/routes/routes';
import type { Submenu } from '@global-types/nav/menuItems';
import { IconBuildings, IconTrendUp, IconReceip, IconBriefcase, IconUserCircle, IconBellRinging, IconMagnifyingGlass } from '@icons';
import { trans } from '@tools/translation.ts';
import Icon from '@components/Icon.vue';
import { storeToRefs } from 'pinia';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Divider from '@components/Divider.vue';
import PlusMenuItemGroup from '@app/layout/nav/mobile/components/PlusMenuItemGroup.vue';
import { useUserProfileStore } from '@stores/useUserProfileStore.ts';
import { MenuItemGroup as MenuItemGroupEnum } from '@enums/menuItemGroups.ts';
import { computed } from 'vue';

const navbarStore = useNavbarStore();
const { isCurrentDropdownOpen, setCurrentDropdownOpen } = navbarStore;

const { hasKYCActive, hasPaySeller } = storeToRefs(useUserProfileStore());

const organizationSubmenus = computed<Submenu[]>(() => {
  const items = [
    { name: trans('user_menu.organization'), link: URL_SELLER_ORGANIZATION_MANAGE },
    { name: trans('user_menu.team'), link: URL_USER_ORGANIZATION_TEAM },
    { name: trans('user_menu.adaf_invoices'), link: URL_USER_PROFILE_INVOICE },
  ];

  if (hasKYCActive.value) {
    items.push(
      { name: trans('user_menu.invoices.management'), link: URL_MANAGEMENT_INVOICES },
      { name: trans('user_menu.gestion_parameters'), link: URL_USER_ORGANIZATION_PAYMENT_METHOD_SELLER },
    );
  }

  return items;
});

const invoicingSubmenus = computed<Submenu[]>(() => {
  const items = [
    { name: trans('user_menu.subscriptions'), link: URL_SUBSCRIPTION },
    { name: trans('user_menu.pay_invoices'), link: URL_INVOICES },
    { name: trans('user_menu.payouts'), link: URL_PAYOUTS },
  ];

  if (hasPaySeller.value) {
    items.push({ name: trans('user_menu.invoices.settings'), link: URL_USER_ORGANIZATION_PAYMENT_ACCOUNT });
  }

  return items;
});
</script>

<template>
  <li>
    <PlusMenuItemGroup
      :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.PROPERTY_HOLDINGS)"
      :submenus="[
        { name: trans('user_menu.offers'), link: URL_USER_OFFERS },
        { name: trans('user_menu.spaces'), link: URL_ADDRESS_PAGE },
      ]"
      @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.PROPERTY_HOLDINGS : null)"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconBuildings"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.property_holdings') }}
      </template>
    </PlusMenuItemGroup>
  </li>

  <li>
    <PlusMenuItemGroup
      :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.SALES)"
      :submenus="[
        { name: trans('user_menu.leads'), link: URL_USER_LEADS },
        { name: trans('user_menu.adaf'), link: URL_ADAF },
      ]"
      @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.SALES : null)"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconTrendUp"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.sales') }}
      </template>
    </PlusMenuItemGroup>
  </li>

  <li>
    <PlusMenuItemGroup
      :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.INVOICING)"
      :has-new-tag="!hasKYCActive"
      :submenus="invoicingSubmenus"
      @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.INVOICING : null)"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconReceip"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.invoicing') }}
      </template>
    </PlusMenuItemGroup>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuItemGroup
      :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.MY_ORGANIZATION)"
      :submenus="organizationSubmenus"
      @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.MY_ORGANIZATION : null)"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconBriefcase"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user.menu.my_organization') }}
      </template>
    </PlusMenuItemGroup>
  </li>

  <li>
    <PlusMenuLink :link="URL_USER_PROFILE_MANAGE">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconUserCircle"
          width="24"
          height="24"
          :stroke="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.profile') }}
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink :link="URL_USER_NOTIFICATIONS">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconBellRinging"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.notifications') }}
      </template>
    </PlusMenuLink>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuLink :link="URL_SEARCH">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconMagnifyingGlass"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.main.see_search') }}
      </template>
    </PlusMenuLink>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>
</template>
